import { Dialog } from '@base-ui/react/dialog';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, PropsWithChildren, useState } from 'react';
import Button from '../Button/Button';
import Modal from '../Modal/Modal';
import SidebarCore, { ISidebarCoreProps } from './SidebarCore';

export type SidebarProps = {
  className?: string;
  isOpen: boolean;
  showCloseControl?: boolean;
  onClose?: () => void;
  title?: string;
  size?: ISidebarCoreProps['size'];
  side?: ISidebarCoreProps['side'];
  behind?: ISidebarCoreProps['behind'];
  zIndexLevel?: ISidebarCoreProps['zIndexLevel'];
  modal?: ISidebarCoreProps['modal'];
  // will display a warning before allowing the user to close the sidebar
  warnUserOnClose?: boolean;
  warnUserContent?: {
    title?: string;
    body?: string;
    confirm?: string;
  };
};

const Sidebar: FC<PropsWithChildren<SidebarProps>> = (props) => {
  const {
    className,
    isOpen,
    showCloseControl = true,
    warnUserOnClose = false,
    warnUserContent = {},
    onClose,
    children,
    title,
    size,
    side,
    behind,
    zIndexLevel,
    modal,
  } = props;

  const [isBlockCloseModalOpen, setIsBlockCloseModalOpen] = useState(false);

  const handleOnClose =
    warnUserOnClose && !!onClose
      ? () => setIsBlockCloseModalOpen(true)
      : onClose;

  return (
    <>
      <SidebarCore
        className={clstx(
          'typography:prose flex max-h-full grow flex-col bg-white shadow-sm border-r border-gray-200',
          className
        )}
        isOpen={isOpen}
        onClose={handleOnClose}
        size={size}
        side={side}
        behind={behind}
        zIndexLevel={zIndexLevel}
        modal={modal}
      >
        {/* header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <Dialog.Title render={<h3 className="text-gray-800" />}>
            {title}
          </Dialog.Title>
          {showCloseControl && (
            <Button
              onClick={handleOnClose}
              variant="secondary"
              className="p-3.5"
            >
              <UntitledIcon name="x-close" className="h-2.5 w-2.5" />
            </Button>
          )}
        </div>

        {/* content */}
        {children}
      </SidebarCore>
      {warnUserOnClose && (
        <Modal
          title={warnUserContent.title || 'Are you sure?'}
          isOpen={isBlockCloseModalOpen}
          onClose={() => setIsBlockCloseModalOpen(false)}
        >
          <Modal.Body>
            <p>
              {warnUserContent.body ||
                'The changes are not saved and will be lost.'}
            </p>
          </Modal.Body>

          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button
                variant="secondary"
                onClick={() => setIsBlockCloseModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                colour="negative"
                onClick={() => {
                  setIsBlockCloseModalOpen(false);
                  onClose?.();
                }}
              >
                {warnUserContent.confirm || 'Discard'}
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

const Body: FC<PropsWithChildren<{ className?: string }>> = ({
  className,
  children,
}) => {
  return (
    <div className={clstx('grow overflow-y-auto', className)}>{children}</div>
  );
};

const Footer: FC<PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
}) => {
  return (
    <div className={clstx('border-t border-gray-200 bg-white p-4', className)}>
      {children}
    </div>
  );
};

export default Object.assign(Sidebar, {
  Body,
  Footer,
});
